import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { InboxOverviewComparison } from 'src/services/__generated__/api'
import { analyticsApi } from 'src/services/analyticsApi'

export interface InboxOverviewSentimentBucket {
  count: number
  percentage: number
}

export interface InboxOverviewSentimentBreakdown {
  negative: InboxOverviewSentimentBucket
  neutral: InboxOverviewSentimentBucket
  positive: InboxOverviewSentimentBucket
  total: number
}

export interface InboxOverviewSourceItem {
  chats: number
  id: string
  messageSentiment: InboxOverviewSentimentBreakdown
  messages: number
  name: string
  sentiment: InboxOverviewSentimentBreakdown
}

export interface InboxOverviewResponseTimeBucket {
  count: number
  id: 'one_to_five_min' | 'over_5m' | 'under_1m'
  label: string
  maxMinutes: number | null
  minMinutes: number
  percentage: number
}

export interface InboxOverviewTopTopic {
  id: string
  messages: number
  sentiment: InboxOverviewSentimentBreakdown
  title: string
}

export interface InboxOverviewTopCampaign {
  chats: number
  id: string
  messages: number
  title: string
}

export interface InboxOverviewTopSource {
  avgMessagesPerChat: number
  avgResponseTime: number
  chats: number
  id: string
  messages: number
  mostMentionedTopics: {
    id: string
    messages: number
    name: string
  }[]
  name: string
  rankLabel: string
  sentiment: InboxOverviewSentimentBreakdown
}

export interface InboxOverviewOrganicVsPaid {
  organic: number
  paid: number
  total: number
}

export interface InboxOverviewNewVsReturning {
  new: number
  returning: number
  total: number
}

export interface InboxOverviewSummaryResponse {
  // "vs previous period" trend badges; null for lifetime / open ranges.
  comparison?: InboxOverviewComparison | null
  newVsReturningUsers?: InboxOverviewNewVsReturning | null
  organicVsPaidChats?: InboxOverviewOrganicVsPaid | null
  platformComparison: {
    items: InboxOverviewSourceItem[]
  }
  responseTimeBreakdown: InboxOverviewResponseTimeBucket[]
  sources: {
    items: InboxOverviewSourceItem[]
    total: number
  }
  topCampaigns: InboxOverviewTopCampaign[]
  topSource: InboxOverviewTopSource | null
  topTopics: InboxOverviewTopTopic[]
  totalChats: InboxOverviewSentimentBreakdown
  totalMessages: InboxOverviewSentimentBreakdown
  totalUsers?: { total: number } | null
}

interface UseGetInboxOverviewSummaryParams {
  fromDate?: string
  platform?: string
  toDate?: string
}

export const useGetInboxOverviewSummaryQuery = (
  params: UseGetInboxOverviewSummaryParams
) => {
  return useQuery<InboxOverviewSummaryResponse | null>({
    // The platform tabs and the date range are part of the key, so switching
    // either is a new query. Without this the data would go undefined for the
    // duration of the request and InboxOverview would fall back to its
    // client-side page aggregate — a different number that then snapped to the
    // API's once the response landed.
    placeholderData: keepPreviousData,

    queryFn: async () => {
      try {
        const response = await analyticsApi().get(
          '/v1_private/inbox-analysis/overview-summary',
          {
            params: {
              fromDate: params.fromDate,
              platform: params.platform,
              toDate: params.toDate,
            },
          }
        )

        return response.data as InboxOverviewSummaryResponse
      } catch (error) {
        console.warn('Inbox overview summary API not available', error)
        return null
      }
    },
    queryKey: [
      QueryKey.GET_INBOX_OVERVIEW_SUMMARY,
      params.platform,
      params.fromDate,
      params.toDate,
    ],
  })
}
